import React, { useCallback, useEffect, useMemo } from 'react';

import { useModifierKey } from '@paragon/client-tools';
import { Point3D as Point3DInterface } from '@paragon/contract-library';

import { useAppDispatch, useAppSelector } from '../../components/hooks';
import { convertFromDesignToLayoutCoordinates } from '../../library/layout-geometry/coordinates';
import { getClosestEntitySnapTarget } from '../../library/layout-snapping/closestTarget';
import { EntitySnapTargetSource } from '../../library/layout-snapping/EntitySnapTargetSource';
import { handlePickedLineInLayoutMode } from '../../store/actions/layout/layoutPickingActions';
import { getGeometricEcosystemForActivePortionOfLayout } from '../../store/selectors/layoutGeometricEcosystemSelectors';
import {
  getCurrentLineSnappingMode,
  getCurrentLineSnappingPreferredSnapType,
} from '../../store/selectors/lineSnappingSelectors';
import { getPickingObjectInfo } from '../../store/selectors/pickingSelectors';
import { clearPickingObject } from '../../store/slices/applicationSlice';
import { useMousePositionInScene } from '../hooks/useMousePositionInScene';
import { threeJsDom } from '../util/events/ThreeJSDom';
import { GlobalThreeJSDomObjectEvent } from '../util/events/types';
import { HoveredEntitySnapVisual } from './HoveredEntitySnapVisual';
import { layoutColors } from './layoutColors';

/**
 * Returns a function to snap a given point to the closest entity snap target. Called a "standard" line snapper
 *   because it uses the user's current line snapping settings from state.
 */
export function useStandardLineSnapper(options?: {
  /** If false, the returned snapper will be inactive and avoid building snap targets for performance. */
  enabled?: boolean;
}) {
  const enabled = options?.enabled ?? true;
  const ecosystem = useAppSelector(getGeometricEcosystemForActivePortionOfLayout);

  const lineSnappingMode = useAppSelector(getCurrentLineSnappingMode);
  const preferredSnapType = useAppSelector(getCurrentLineSnappingPreferredSnapType);

  const targets = useMemo(
    () => (enabled ? new EntitySnapTargetSource(ecosystem).getActiveTargetsForLineSnapping(lineSnappingMode) : null),
    [enabled, ecosystem, lineSnappingMode],
  );

  return useCallback(
    (sourcePosition: Point3DInterface) => {
      if (targets == null) return null;

      const mousePosition = convertFromDesignToLayoutCoordinates(sourcePosition).projectTo2D();
      return getClosestEntitySnapTarget(targets, mousePosition, { preferredSnapType });
    },
    [targets, preferredSnapType],
  );
}

export function LinePickingVisualsAndEventListeners() {
  const dispatch = useAppDispatch();

  const mousePosition = useMousePositionInScene();
  const pickingLineInfo = useAppSelector((state) => {
    const info = getPickingObjectInfo(state);
    return info?.type === 'pickingLayoutLine' ? info : null;
  });
  const pickingLineTarget = pickingLineInfo?.target ?? null;

  const snapToLine = useStandardLineSnapper({ enabled: pickingLineTarget != null });

  const shiftHeld = useModifierKey('shiftKey');
  const shiftCanForceAbsolute =
    pickingLineTarget === 'roof-plane-definition' || pickingLineTarget === 'ceiling-plane-definition';

  useEffect(() => {
    if (pickingLineInfo?.target === 'envelope-trim-or-extend-line' && pickingLineInfo.repeat && !shiftHeld) {
      dispatch(clearPickingObject());
    }
  }, [dispatch, pickingLineInfo, shiftHeld]);

  useEffect(() => {
    if (pickingLineTarget == null) return;

    function onSceneClick(event: GlobalThreeJSDomObjectEvent) {
      const pickedSnap = snapToLine(event.eventPosition.worldPosition);
      if (pickedSnap == null) return;

      dispatch(handlePickedLineInLayoutMode(pickedSnap, { event }));
      return false; // stop propagation
    }

    threeJsDom.addGlobalListener('click', onSceneClick);
    return () => void threeJsDom.removeGlobalListener('click', onSceneClick);
  }, [dispatch, pickingLineTarget, snapToLine]);

  if (mousePosition == null) return null;
  if (pickingLineTarget == null) return null;

  const previewSnap = snapToLine(mousePosition);
  if (!previewSnap) return null;

  const color = shiftCanForceAbsolute && shiftHeld ? layoutColors.entitySnapForceAbsolute : layoutColors.entitySnap;

  return <HoveredEntitySnapVisual snap={previewSnap} color={color} />;
}
